
import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { formatDistanceToNow } from 'date-fns';
import { Calendar, User, Eye, MessageSquare, Edit, Trash2 } from 'lucide-react';
import ProgressIndicator from '@/components/gamification/ProgressIndicator';
import { useAuth } from '@/hooks/useAuth';
import { useRoleAccess } from '@/hooks/useRoleAccess';
import type { Tables } from '@/integrations/supabase/types';

interface NodeCardProps {
  node: Tables<'nodes'> & {
    profiles?: {
      username?: string | null;
      full_name?: string | null;
    } | null;
    disciplines?: {
      name: string;
      slug: string;
    } | null;
  };
  onEdit?: (nodeId: string) => void;
  onDelete?: (nodeId: string) => void;
  showActions?: boolean;
}

const NodeCard: React.FC<NodeCardProps> = ({ 
  node, 
  onEdit, 
  onDelete, 
  showActions = false 
}) => {
  const { user } = useAuth();
  const { canEditContent } = useRoleAccess();

  const canEdit = canEditContent || (user && node.author_id === user.id);
  const canDelete = canEditContent || (user && node.author_id === user.id && node.status === 'draft');

  // Extract preview from content if it exists
  const getPreview = () => {
    if (!node.content) return null;
    
    try {
      if (typeof node.content === 'object' && node.content !== null) {
        const contentObj = node.content as any;
        return contentObj.preview || contentObj.text?.substring(0, 150) + '...' || null;
      }
      if (typeof node.content === 'string') {
        return node.content.substring(0, 150) + '...';
      }
    } catch (error) {
      console.error('Error parsing content preview:', error);
    }
    
    return null;
  };

  const preview = getPreview();

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <CardTitle className="text-lg mb-2">
              <Link 
                to={`/node/${node.id}`}
                className="hover:text-primary transition-colors"
              >
                {node.title}
              </Link>
            </CardTitle>
            <div className="flex items-center gap-4 text-sm text-black">
              <div className="flex items-center gap-1">
                <User className="h-4 w-4" />
                {node.profiles?.full_name || node.profiles?.username || 'Anonymous'}
              </div>
              <div className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                {formatDistanceToNow(new Date(node.created_at), { addSuffix: true })}
              </div>
            </div>
          </div>
          <div className="flex flex-col items-end gap-2">
            <div className="flex items-center gap-2">
              <Badge variant="secondary">
                {node.node_type}
              </Badge>
              <Badge variant={node.status === 'published' ? 'default' : 'outline'}>
                {node.status}
              </Badge>
            </div>
            {node.disciplines && (
              <Badge variant="outline">
                {node.disciplines.name}
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>
      
      <CardContent>
        <div className="space-y-4">
          {preview && (
            <p className="text-black line-clamp-2">
              {preview}
            </p>
          )}
          
          {/* Progress Indicator */}
          {user && node.status === 'published' && (
            <ProgressIndicator 
              nodeId={node.id} 
              variant="bar" 
              showTimeSpent={true}
              className="border-t pt-3"
            />
          )}
          
          {/* Actions */}
          {showActions && (canEdit || canDelete) && (
            <div className="flex items-center gap-2 border-t pt-3">
              {canEdit && onEdit && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onEdit(node.id)}
                >
                  <Edit className="h-4 w-4 mr-1" />
                  Edit
                </Button>
              )}
              {canDelete && onDelete && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onDelete(node.id)}
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Delete
                </Button>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default NodeCard;
